import { useState, useEffect } from 'react';
import { Plus, Copy, Ban, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Label } from '../ui/label';

interface InviteCode {
  id: number;
  code: string;
  created_by: number;
  created_by_username: string;
  created_at: string;
  used_by: number | null;
  used_by_username: string | null;
  used_at: string | null;
  is_active: number;
  max_uses: number;
  current_uses: number;
  expires_at: string | null;
}

export function InviteManagement() {
  const [invites, setInvites] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [maxUses, setMaxUses] = useState(1);
  const [expiresIn, setExpiresIn] = useState(7);

  const fetchInvites = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/invites?page=${page}&limit=20`);
      const data = await response.json();

      setInvites(data.invites || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, [page]);

  const handleGenerate = async () => {
    try {
      // 确保 expiresIn 是有效的数字，如果是 NaN 或 0 则设为 undefined
      const validExpiresIn = expiresIn && !isNaN(expiresIn) && expiresIn > 0 ? expiresIn : undefined;

      const response = await fetch('/api/admin/invites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maxUses, expiresIn: validExpiresIn }),
      });

      if (response.ok) {
        const data = await response.json();
        alert(`邀请码生成成功: ${data.code}`);
        setDialogOpen(false);
        fetchInvites();
      } else {
        const error = await response.json();
        alert(`生成失败: ${error.message || error.error}`);
      }
    } catch (error) {
      console.error('Failed to generate invite:', error);
      alert('生成失败，请重试');
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    alert('邀请码已复制到剪贴板');
  };

  const handleDisable = async (code: string) => {
    try {
      const response = await fetch('/api/admin/invites', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      if (response.ok) {
        fetchInvites();
      }
    } catch (error) {
      console.error('Failed to disable invite:', error);
    }
  };

  const handleDelete = async (code: string) => {
    if (!confirm('确定要删除这个邀请码吗?')) return;

    try {
      const response = await fetch(`/api/admin/invites?code=${code}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchInvites();
      }
    } catch (error) {
      console.error('Failed to delete invite:', error);
    }
  };

  const getStatusBadge = (invite: InviteCode) => {
    if (!invite.is_active) {
      return <span className="px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground">已禁用</span>;
    }
    if (invite.current_uses >= invite.max_uses) {
      return <span className="px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground">已用完</span>;
    }
    if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
      return <span className="px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground">已过期</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs bg-primary text-primary-foreground">可用</span>;
  };

  return (
    <Card className="relative">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>邀请码管理</CardTitle>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              生成邀请码
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>生成邀请码</DialogTitle>
              <DialogDescription>
                设置邀请码的使用次数和有效期
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="maxUses">最大使用次数</Label>
                <Input
                  id="maxUses"
                  type="number"
                  min="1"
                  value={maxUses}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setMaxUses(isNaN(val) ? 1 : val);
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="expiresIn">有效期 (天，0表示永久)</Label>
                <Input
                  id="expiresIn"
                  type="number"
                  min="0"
                  value={expiresIn}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setExpiresIn(isNaN(val) ? 0 : val);
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleGenerate}>生成</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">加载中...</div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>邀请码</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>使用情况</TableHead>
                  <TableHead>创建者</TableHead>
                  <TableHead>创建时间</TableHead>
                  <TableHead>过期时间</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((invite) => (
                  <TableRow key={invite.id}>
                    <TableCell className="font-mono">{invite.code}</TableCell>
                    <TableCell>{getStatusBadge(invite)}</TableCell>
                    <TableCell>
                      {invite.current_uses} / {invite.max_uses}
                    </TableCell>
                    <TableCell>{invite.created_by_username}</TableCell>
                    <TableCell>{new Date(invite.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {invite.expires_at
                        ? new Date(invite.expires_at).toLocaleDateString()
                        : '永久'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopy(invite.code)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {invite.is_active && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDisable(invite.code)}
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(invite.code)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* 分页 */}
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                上一页
              </Button>
              <span className="flex items-center px-4">
                第 {page} / {totalPages} 页
              </span>
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                下一页
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

